/**
 * Enume — API Service Layer
 * ═══════════════════════════════
 * Single source of truth for all backend communication.
 *
 * LOCAL DEV:  baseURL = '/api' (Vite proxy → http://localhost:5000/api)
 * PRODUCTION: baseURL = VITE_API_URL (e.g. 'https://your-backend.azurewebsites.net/api')
 *
 * Token keys:
 *   'red_social_token'         — short-lived JWT access token (30 min)
 *   'red_social_refresh_token' — long-lived refresh token (7 days)
 *
 * S8: When an API call gets a 401, the interceptor silently sends the
 * refresh token to /auth/refresh, stores the new access token, and retries
 * the original request. The user never sees a login screen unless the
 * refresh token itself has expired (7 days of inactivity).
 */

import axios from 'axios';
import { API_BASE } from '../config/apiBase';

// ============================================================================
// AXIOS INSTANCE
// ============================================================================

// Resolved once in src/config/apiBase.js, which normalizes the trailing /api
// and exports API_ORIGIN alongside it for SSE and stored media paths.
// Do not read import.meta.env.VITE_API_URL directly anywhere else.
const baseURL = API_BASE;

export const api = axios.create({
  baseURL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

// ── Cache-busting interceptor ───────────────────────────────────────
// Appends a unique timestamp to every GET request so Azure CDN / browser
// cache can't serve stale responses after a deploy.
api.interceptors.request.use((config) => {
  if (config.method === 'get') {
    config.params = { ...config.params, _v: Date.now() };
  }
  return config;
});

// Inject JWT on every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('red_social_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ── S8: Silent refresh-on-401 ───────────────────────────────────────
// When an access token expires mid-session, the first 401 triggers a
// refresh. A mutex (_isRefreshing) prevents multiple concurrent refresh
// calls; queued requests wait for the refresh to complete, then retry.

let _isRefreshing = false;
let _refreshSubscribers = [];

function _onRefreshed(newToken) {
  _refreshSubscribers.forEach((cb) => cb(newToken));
  _refreshSubscribers = [];
}

function _addRefreshSubscriber(callback) {
  _refreshSubscribers.push(callback);
}

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const originalRequest = err.config;

    // Only attempt refresh on 401 from non-auth endpoints, and only once
    if (
      err.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/login') &&
      !originalRequest.url?.includes('/auth/register') &&
      !originalRequest.url?.includes('/auth/verify') &&
      !originalRequest.url?.includes('/auth/refresh')
    ) {
      const refreshToken = localStorage.getItem('red_social_refresh_token');

      if (!refreshToken) {
        // No refresh token — can't recover, go to login
        localStorage.removeItem('red_social_token');
        localStorage.removeItem('red_social_refresh_token');
        window.location.href = '/login';
        return Promise.reject(err);
      }

      if (_isRefreshing) {
        // Another request is already refreshing — queue this one
        return new Promise((resolve) => {
          _addRefreshSubscriber((newToken) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            resolve(api(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      _isRefreshing = true;

      try {
        // Call /auth/refresh WITHOUT the interceptor (use raw axios to
        // avoid infinite loops if the refresh itself 401s)
        const response = await axios.post(
          `${baseURL}/auth/refresh`,
          { refresh_token: refreshToken },
          { headers: { 'Content-Type': 'application/json' } }
        );

        const newAccessToken = response.data.access_token;
        localStorage.setItem('red_social_token', newAccessToken);

        // Notify all queued requests
        _onRefreshed(newAccessToken);

        // Retry the original request with the new token
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } catch (refreshErr) {
        // Refresh failed — token expired or revoked, go to login
        localStorage.removeItem('red_social_token');
        localStorage.removeItem('red_social_refresh_token');
        _refreshSubscribers = [];
        window.location.href = '/login';
        return Promise.reject(refreshErr);
      } finally {
        _isRefreshing = false;
      }
    }

    // Non-401 errors, or auth endpoints — pass through
    return Promise.reject(err);
  }
);

// ============================================================================
// AUTH API — prefix: /auth
// ============================================================================

export const authAPI = {
  login:    (email, password)           => api.post('/auth/login', { email, password }),
  register: (email, password, username, dateOfBirth, turnstileToken, campusId) =>
    api.post('/auth/register', { email, password, username, dateOfBirth, turnstileToken, campus_id: campusId }),
  verify:   ()                          => api.get('/auth/verify'),
  me:       ()                          => api.get('/auth/me'),

  updateProfile: (data) => api.put('/auth/me', data),
  uploadAvatar:  (formData) => api.post('/auth/me/avatar', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  changePassword: (data) => api.put('/auth/me/password', data),

  // ── Password reset ──────────────────────────────────────
  forgotPassword:  (email) => api.post('/auth/forgot-password', { email }),
  resetPassword:   (token, newPassword) => api.post('/auth/reset-password', { token, newPassword }),

  // ── S8: Token refresh & logout ──────────────────────────
  refresh:  (refreshToken) => api.post('/auth/refresh', { refresh_token: refreshToken }),
  logout:   (refreshToken) => api.post('/auth/logout', { refresh_token: refreshToken }),
};

// ============================================================================
// PROJECTS API — prefix: /projects
// ============================================================================

export const projectsAPI = {
  getAll:     (params = {})  => api.get('/projects', { params }),
  getById:    (id)           => api.get(`/projects/${id}`),
  create:     (formData)     => api.post('/projects', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  update:     (id, formData) => api.put(`/projects/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  delete:     (id)           => api.delete(`/projects/${id}`),
  fork:       (id, formData) => api.post(`/projects/${id}/fork`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  search:     (params = {})  => api.get('/projects/search', { params }),
  getTags:    ()             => api.get('/projects/tags'),

  // Lineage
  getLineage:   (id) => api.get(`/projects/${id}/lineage`),
  getAncestors: (id) => api.get(`/projects/${id}/ancestors`),

  // Curated branches
  createCuratedBranch:        (data)        => api.post('/projects/curated-branches', data),
  getUserBranches:            (userId)      => api.get(`/projects/users/${userId}/public-curated-branches`),
  getFeaturedBranches:        ()            => api.get('/projects/featured-curated-branches'),
  updateBranch:               (id, data)    => api.put(`/projects/curated-branch/${id}`, data),
  deleteBranch:               (id)          => api.delete(`/projects/curated-branch/${id}`),
  getContainingBranches:      (projectId)   => api.get(`/projects/${projectId}/containing-branches`),

  // Versions
  getVersions:  (id) => api.get(`/projects/${id}/versions`),
  createVersion: (id, formData) => api.post(`/projects/${id}/versions`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),

  // Downloads
  downloadFile: (id, filename) => api.get(`/projects/${id}/download/${filename}`, { responseType: 'blob' }),

  // Views
  trackView: (id) => api.post(`/projects/${id}/track-view`),

  // Direct large-file upload (bypasses Flask body-size limit)
  generateUploadUrl: (filename) =>
    api.post('/projects/generate-upload-url', { filename }),

  uploadFileDirect: async (uploadUrl, file, onProgress) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', uploadUrl, true);
      xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
      if (onProgress) {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
        };
      }
      xhr.onload = () => xhr.status < 300 ? resolve() : reject(new Error(`Upload failed: ${xhr.status}`));
      xhr.onerror = () => reject(new Error('Upload failed: network error'));
      xhr.send(file);
    });
  },

  finalizeUpload: (projectId, blobUrl, originalFilename, fileSize) =>
    api.post(`/projects/${projectId}/finalize-upload`, {
      blob_url: blobUrl,
      original_filename: originalFilename,
      file_size: fileSize,
    }),
};

// ============================================================================
// CHALLENGES API — prefix: /challenges
// ============================================================================

export const challengesAPI = {
  getAll:       (params = {})  => api.get('/challenges', { params }),
  getById:      (id)           => api.get(`/challenges/${id}`),
  create:       (data)         => api.post('/challenges', data),
  update:       (id, data)     => api.put(`/challenges/${id}`, data),
  delete:       (id)           => api.delete(`/challenges/${id}`),
  respond:      (id, formData) => api.post(`/challenges/${id}/respond`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  getResponses: (id)           => api.get(`/challenges/${id}/responses`),
};

// ============================================================================
// COLLABORATION REQUESTS API
// ============================================================================

export const collaborationAPI = {
  create:       (data)     => api.post('/collaboration-requests', data),
  getIncoming:  ()         => api.get('/collaboration-requests/incoming'),
  getOutgoing:  ()         => api.get('/collaboration-requests/outgoing'),
  accept:       (id)       => api.post(`/collaboration-requests/${id}/accept`),
  reject:       (id)       => api.post(`/collaboration-requests/${id}/reject`),
  cancel:       (id)       => api.delete(`/collaboration-requests/${id}/cancel`),
  getUserStats: (userId)   => api.get(`/users/${userId}/collaboration-stats`),
};

// ============================================================================
// MESSAGING API — prefix: /messaging
// ============================================================================

export const messagingAPI = {
  // Channels
  getChannels:    ()             => api.get('/messaging/channels'),
  createChannel:  (data)         => api.post('/messaging/channels', data),
  getChannel:     (id)           => api.get(`/messaging/channels/${id}`),
  joinChannel:    (id)           => api.post(`/messaging/channels/${id}/join`),
  leaveChannel:   (id)           => api.post(`/messaging/channels/${id}/leave`),
  addMembers:     (id, data)     => api.post(`/messaging/channels/${id}/members`, data),

  // Messages
  getMessages:    (channelId, params = {}) => api.get(`/messaging/channels/${channelId}/messages`, { params }),
  sendMessage:    (channelId, data) => {
    if (data instanceof FormData) {
      return api.post(`/messaging/channels/${channelId}/messages`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    }
    return api.post(`/messaging/channels/${channelId}/messages`, data);
  },
  editMessage:    (messageId, data) => api.put(`/messaging/messages/${messageId}`, data),
  deleteMessage:  (messageId)       => api.delete(`/messaging/messages/${messageId}`),
  pinMessage:     (messageId)       => api.post(`/messaging/messages/${messageId}/pin`),
  addReaction:    (messageId, data) => api.post(`/messaging/messages/${messageId}/reactions`, data),
  removeReaction: (messageId, emoji) => api.delete(`/messaging/messages/${messageId}/reactions/${emoji}`),

  // Direct messages
  getOrCreateDM:  (userId) => api.post('/messaging/dm', { user_id: userId }),
  getConversations: ()     => api.get('/messaging/conversations'),

  // Read receipts
  markRead:       (channelId) => api.post(`/messaging/channels/${channelId}/read`),
  getUnreadCount: ()          => api.get('/messaging/unread-count'),
};

// ============================================================================
// USERS API — prefix: /users
// ============================================================================

export const usersAPI = {
  getProfile:    (userId)      => api.get(`/users/${userId}`),
  search:        (query)       => api.get('/users/search', { params: { q: query } }),
  getProjects:   (userId)      => api.get('/projects', { params: { user_id: userId } }),
};

// ============================================================================
// NOTIFICATIONS API — prefix: /notifications
// ============================================================================

export const notificationsAPI = {
  getAll:       (params = {}) => api.get('/notifications', { params }),
  markRead:     (id)          => api.post(`/notifications/${id}/read`),
  markAllRead:  ()            => api.post('/notifications/read-all'),
  getUnread:    ()            => api.get('/notifications/unread-count'),
};

// ============================================================================
// MAP API — prefix: /map
// ============================================================================

export const mapAPI = {
  getProjects:   (params = {}) => api.get('/map/projects', { params }),
  getRegions:    ()            => api.get('/map/regions'),
  getCategories: ()            => api.get('/map/categories'),
  getNearby:     (lat, lng, params = {}) => api.get('/map/nearby', { params: { lat, lng, ...params } }),
};

// ============================================================================
// ANALYTICS API
// ============================================================================

export const analyticsAPI = {
  trackView:                (projectId) => api.post(`/projects/${projectId}/track-view`),
  getProjectViewers:        (projectId, params = {}) => api.get(`/projects/${projectId}/viewers`, { params }),
  getPortfolioViews:        ()          => api.get('/my/portfolio-views'),
  getChallengeAnalytics:    (id)        => api.get(`/challenges/${id}/analytics`),
  getMyChallengeAnalytics:  ()          => api.get('/my/challenge-analytics'),
  getCollaborationAnalytics: ()         => api.get('/my/collaboration-analytics'),
  getPortfolioSummary:      ()          => api.get('/my/portfolio-summary'),
  getPersonal:              ()          => api.get('/personal'),
  getCampus:                ()          => api.get('/campus'),
};

// ============================================================================
// MILESTONES API
// ============================================================================

export const milestonesAPI = {
  update: (milestoneId, data)  => api.put(`/milestones/${milestoneId}`, data),
  delete: (milestoneId)        => api.delete(`/milestones/${milestoneId}`),
};

// ============================================================================
// UPDATES API
// ============================================================================

export const updatesAPI = {
  getFeed: (params = {})       => api.get('/updates/feed', { params }),
  update:  (updateId, data)    => api.put(`/updates/${updateId}`, data),
};

// ============================================================================
// COMMENTS & MARKETPLACE API — prefix: /comments-marketplace
// ============================================================================

export const commentsMarketplaceAPI = {
  get:  (path, params) => api.get(`/comments-marketplace${path}`, { params }),
  post: (path, data)   => api.post(`/comments-marketplace${path}`, data),
};

// ============================================================================
// CITATIONS API — prefix: /citations
// ============================================================================

export const citationsAPI = {
  get:  (path, params) => api.get(`/citations${path}`, { params }),
  post: (path, data)   => api.post(`/citations${path}`, data),
};

// ============================================================================
// REALTIME API — prefix: /realtime
// ============================================================================

export const realtimeAPI = {
  subscribe: (params = {}) => api.get('/realtime/subscribe', { params }),
};

// ============================================================================
// USES API — one project, many contexts
// Full paths under /api (blueprint has no prefix). See backend/app/uses_api.py
// ============================================================================

export const usesAPI = {
  listForProject: (projectId)             => api.get(`/projects/${projectId}/uses`),
  create:         (projectId, data)       => api.post(`/projects/${projectId}/uses`, data),
  update:         (useId, data)           => api.patch(`/uses/${useId}`, data),
  remove:         (useId)                 => api.delete(`/uses/${useId}`),
  setHidden:      (useId, hidden)         => api.post(`/uses/${useId}/visibility`, { hidden }),
  mapEchoes:      (params = {})           => api.get('/uses/map', { params }),
  getComments:    (useId)                 => api.get(`/uses/${useId}/comments`),
  addComment:     (useId, content, parentCommentId = null) =>
    api.post(`/uses/${useId}/comments`, { content, parentCommentId }),
  deleteComment:  (commentId)             => api.delete(`/use-comments/${commentId}`),
};

// ============================================================================
// COLLECTIONS API — smart folders: rule + pins - exclusions
// (Distinct from curated branches, which live under projectsAPI.)
// ============================================================================

export const collectionsAPI = {
  listMine:   ()                          => api.get('/collections'),
  create:     (data)                      => api.post('/collections', data),
  get:        (id, params = {})           => api.get(`/collections/${id}`, { params }),
  update:     (id, data)                  => api.patch(`/collections/${id}`, data),
  remove:     (id)                        => api.delete(`/collections/${id}`),
  setItem:    (id, projectId, mode = 'pin') => api.put(`/collections/${id}/items/${projectId}`, { mode }),
  clearItem:  (id, projectId)             => api.delete(`/collections/${id}/items/${projectId}`),
};

// ============================================================================
// MENTIONS API — backlinks: [[p:123]] in descriptions
// Full paths under /api (blueprint has no prefix). See backend/app/mentions_api.py
// ============================================================================

export const mentionsAPI = {
  connections: (projectId)          => api.get(`/projects/${projectId}/connections`),
  search:      (q, limit = 8)       => api.get('/mentions/search', { params: { q, limit } }),
  setHidden:   (mentionId, hidden)  => api.post(`/mentions/${mentionId}/visibility`, { hidden }),
  mapEdges:    ()                   => api.get('/mentions/map'),
};

// Default export is the raw axios instance for one-off calls
export default api;